import React, { useEffect, useMemo, useRef, useState } from "react";
import { addStudent, bulkAddStudents, deleteStudent, getClasses, getStudents, updateStudent } from "../services/api";
import DateInput from "./DateInput";
import { downloadCsvTemplate, readCsvFile } from "../utils/bulkImport";
import { confirmAppAction, showAppToast } from "../utils/feedback";
import { formatDate, toInputDate } from "../utils/helpers";
import "./StudentList.css";

const emptyStudentForm = {
  name: "",
  class: "",
  parentPhone: "",
  rollNumber: "",
  dob: "",
  address: "",
  schoolName: "",
  joiningDate: "",
  studentMobileNumber: "",
  board: "",
  course: "",
  fees: "",
  imageData: ""
};

const readJpegFile = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }

    const allowedTypes = ["image/jpeg", "image/jpg"];
    const lowerName = file.name.toLowerCase();
    const hasValidExtension = lowerName.endsWith(".jpg") || lowerName.endsWith(".jpeg");

    if (!allowedTypes.includes(file.type) && !hasValidExtension) {
      reject(new Error("Only JPG and JPEG images are allowed"));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Failed to read image file"));
    reader.readAsDataURL(file);
  });

const normalizeCourse = (value) => {
  const trimmed = String(value || "").trim();
  return trimmed === "Fees" ? "General" : trimmed;
};

function StudentList({ searchSelection, onOpenProfile }) {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("all");
  const [selectedBoard, setSelectedBoard] = useState("all");
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [viewMode, setViewMode] = useState("card");
  const [formData, setFormData] = useState(emptyStudentForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [bulkFileKey, setBulkFileKey] = useState(0);
  const formSectionRef = useRef(null);

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    fetchStudents();
  }, [selectedClass]);

  useEffect(() => {
    if (searchSelection?.type === "student") {
      setSelectedClass(searchSelection.filterValue || "all");
      setViewMode("detail");
    }
  }, [searchSelection]);

  useEffect(() => {
    if (searchSelection?.type === "student" && searchSelection.action === "edit" && students.length > 0) {
      const matchedStudent = students.find((student) => student._id === searchSelection.id);
      if (matchedStudent) {
        handleEditStudent(matchedStudent);
      }
    }
  }, [searchSelection, students]);

  useEffect(() => {
    if (!showForm) {
      return;
    }

    const timer = window.setTimeout(() => {
      formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [showForm, editingId]);

  const fetchClasses = async () => {
    try {
      const res = await getClasses();
      setClasses(res.data);
    } catch (err) {
      console.error("Error fetching classes:", err);
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const res = await getStudents(selectedClass, { includeImages: true });
      setStudents(res.data);
    } catch (err) {
      console.error("Error fetching students:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];

    try {
      const imageData = await readJpegFile(file);
      setFormData({ ...formData, imageData });
    } catch (err) {
      showAppToast({ type: "error", title: "Image upload failed", message: err.message });
      setFileInputKey((current) => current + 1);
    }
  };

  const resetForm = () => {
    setFormData(emptyStudentForm);
    setEditingId(null);
    setShowForm(false);
    setFileInputKey((current) => current + 1);
  };

  const handleDownloadTemplate = () => {
    downloadCsvTemplate(
      "students-bulk-template.csv",
      [
        "name",
        "class",
        "parentPhone",
        "rollNumber",
        "dob",
        "address",
        "schoolName",
        "joiningDate",
        "studentMobileNumber",
        "board",
        "course",
        "fees"
      ],
      [
        {
          name: "Amit Kumar",
          class: "12",
          parentPhone: "9876543210",
          rollNumber: "101",
          dob: "14-05-2008",
          address: "Kadodara, Surat",
          schoolName: "Bright School",
          joiningDate: "01-04-2026",
          studentMobileNumber: "9876543200",
          board: "CBSE",
          course: "Science",
          fees: "25000"
        },
        {
          name: "Priya Sharma",
          class: "12",
          parentPhone: "9876543211",
          rollNumber: "102",
          dob: "21-08-2008",
          address: "Palsana, Surat",
          schoolName: "Vidya Mandir",
          joiningDate: "03-04-2026",
          studentMobileNumber: "9876543201",
          board: "GSEB",
          course: "Commerce",
          fees: "22000"
        }
      ]
    );
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const rows = await readCsvFile(file);
      const studentsPayload = rows.map((row) => ({
        name: row.name,
        class: row.class,
        parentPhone: row.parentPhone,
        rollNumber: row.rollNumber,
        dob: toInputDate(row.dob),
        address: row.address,
        schoolName: row.schoolName,
        joiningDate: toInputDate(row.joiningDate),
        studentMobileNumber: row.studentMobileNumber,
        board: row.board,
        course: row.course,
        fees: row.fees
      }));

      if (studentsPayload.length === 0) {
        showAppToast({
          type: "error",
          title: "No students found",
          message: "The uploaded file does not contain any student rows."
        });
        return;
      }

      await bulkAddStudents({ students: studentsPayload });
      showAppToast({
        type: "success",
        title: "Students uploaded",
        message: `${studentsPayload.length} student records were added successfully.`
      });
      fetchStudents();
      fetchClasses();
      setBulkFileKey((current) => current + 1);
    } catch (err) {
      console.error("Bulk upload failed:", err);
      showAppToast({
        type: "error",
        title: "Upload failed",
        message: "Please use the sample template columns for the student file."
      });
      setBulkFileKey((current) => current + 1);
    }
  };

  const handleAddStudent = async () => {
    if (!formData.name || !formData.class || !formData.parentPhone || !formData.rollNumber) {
      showAppToast({
        type: "error",
        title: "Missing details",
        message: "Please fill in all required student fields before saving."
      });
      return;
    }

    const payload = {
      name: formData.name,
      class: formData.class,
      parentPhone: formData.parentPhone,
      rollNumber: formData.rollNumber,
      dob: formData.dob,
      address: formData.address,
      schoolName: formData.schoolName,
      joiningDate: formData.joiningDate,
      studentMobileNumber: formData.studentMobileNumber,
      board: formData.board,
      course: formData.course,
      fees: formData.fees,
      imageData: formData.imageData
    };

    try {
      if (editingId) {
        await updateStudent(editingId, payload);
        showAppToast({
          type: "success",
          title: "Student updated",
          message: `${formData.name} was updated successfully.`
        });
      } else {
        await addStudent(payload);
        showAppToast({
          type: "success",
          title: "Student added",
          message: `${formData.name} was added successfully.`
        });
      }

      resetForm();
      fetchStudents();
      fetchClasses();
    } catch (err) {
      console.error("Error saving student:", err);
      showAppToast({
        type: "error",
        title: "Failed to save student",
        message: err.response?.data?.error || err.message
      });
    }
  };

  const handleEditStudent = (student) => {
    setFormData({
      name: student.name || "",
      class: student.class || "",
      parentPhone: student.parentPhone || "",
      rollNumber: student.rollNumber || "",
      dob: toInputDate(student.dob),
      address: student.address || "",
      schoolName: student.schoolName || "",
      joiningDate: toInputDate(student.joiningDate),
      studentMobileNumber: student.studentMobileNumber || "",
      board: student.board || "",
      course: student.course || "",
      fees: student.fees || "",
      imageData: student.imageData || student.imageUrl || ""
    });
    setEditingId(student._id);
    setShowForm(true);
    setFileInputKey((current) => current + 1);
  };

  const handleDeleteStudent = async (id) => {
    const targetStudent = students.find((student) => student._id === id);
    const confirmed = await confirmAppAction({
      title: "Delete student",
      message: `This will permanently remove ${targetStudent?.name || "this student"} from the system.`,
      confirmLabel: "Delete"
    });

    if (!confirmed) {
      return;
    }

    try {
      await deleteStudent(id);
      showAppToast({
        type: "success",
        title: "Student deleted",
        message: `${targetStudent?.name || "The student"} was removed successfully.`
      });
      fetchStudents();
      fetchClasses();
    } catch (err) {
      console.error("Error deleting student:", err);
      showAppToast({
        type: "error",
        title: "Failed to delete student",
        message: err.response?.data?.error || err.message || "The student could not be removed."
      });
    }
  };

  const filteredStudents = useMemo(() => students.filter((student) => {
    const matchesBoard = selectedBoard === "all" || (student.board || "") === selectedBoard;
    const matchesCourse = selectedCourse === "all" || normalizeCourse(student.course) === selectedCourse;
    return matchesBoard && matchesCourse;
  }), [students, selectedBoard, selectedCourse]);

  const boardOptions = ["CBSE", "GSEB"];
  const courseOptions = ["Science", "Commerce", "General"];

  const studentCount = filteredStudents.length;
  const uniqueParents = useMemo(
    () => new Set(filteredStudents.map((student) => student.parentPhone)).size,
    [filteredStudents]
  );
  const openProfile = (student) => {
    if (!onOpenProfile) {
      return;
    }

    onOpenProfile({
      id: student._id,
      type: "student",
      title: student.name,
      subtitle: `Class ${student.class} • Roll ${student.rollNumber}`,
      meta: student.parentPhone,
      filterValue: student.class
    });
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Students Dashboard</h1>
        {/* <p>Manage students in cards and upload JPG/JPEG profile photos.</p> */}
      </div>

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Students in {selectedClass === "all" ? "All Classes" : selectedClass}</h3>
          <p className="stat-number">{studentCount}</p>
        </div>
        <div className="stat-card">
          <h3>Unique Parents</h3>
          <p className="stat-number">{uniqueParents}</p>
        </div>
        <div className="stat-card">
          <h3>Classes Listed</h3>
          <p className="stat-number">{classes.length}</p>
        </div>
      </div>

      <div className="filter-section">
        <div className="toolbar-group toolbar-group--left">
          <div className="filter-container filter-container--compact">
            <label htmlFor="class-select" className="filter-label">Class</label>
            <select
              id="class-select"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="class-select"
            >
              <option value="all">All Classes</option>
              {classes.map((cls) => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>
          <div className="filter-container filter-container--compact">
            <label htmlFor="board-select" className="filter-label">Board</label>
            <select
              id="board-select"
              value={selectedBoard}
              onChange={(e) => setSelectedBoard(e.target.value)}
              className="class-select"
            >
              <option value="all">All Boards</option>
              {boardOptions.map((board) => (
                <option key={board} value={board}>{board}</option>
              ))}
            </select>
          </div>
          <div className="filter-container filter-container--compact">
            <label htmlFor="course-select" className="filter-label">Course</label>
            <select
              id="course-select"
              value={selectedCourse}
              onChange={(e) => setSelectedCourse(e.target.value)}
              className="class-select"
            >
              <option value="all">All Courses</option>
              {courseOptions.map((course) => (
                <option key={course} value={course}>{course}</option>
              ))}
            </select>
          </div>
          {!showForm && (
            <div className="bulk-tools">
              <button className="btn btn-success btn--compact" onClick={() => setShowForm(true)}>
                Add Student
              </button>
              <button className="btn btn-secondary btn--compact" onClick={handleDownloadTemplate}>
                Sample Excel
              </button>
              <label className="btn btn-primary btn--compact bulk-tools__upload">
                Bulk Upload
                <input
                  key={bulkFileKey}
                  type="file"
                  accept=".csv"
                  onChange={handleBulkUpload}
                />
              </label>
            </div>
          )}
        </div>
        <div className="toolbar-group toolbar-group--right">
          <div className="view-toggle">
            <span className="view-toggle__label">View</span>
            <button
              className={`view-toggle__btn ${viewMode === "card" ? "active" : ""}`}
              onClick={() => setViewMode("card")}
            >
              Card
            </button>
            <button
              className={`view-toggle__btn ${viewMode === "detail" ? "active" : ""}`}
              onClick={() => setViewMode("detail")}
            >
              Detail
            </button>
          </div>
        </div>
      </div>

      <div className="main-content">
        {showForm && (
          <div className="form-section" ref={formSectionRef}>
            <h3>{editingId ? "Edit Student" : "Add New Student"}</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Student Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter student name" />
              </div>
              <div className="form-group">
                <label>Class</label>
                <input type="text" name="class" value={formData.class} onChange={handleInputChange} placeholder="e.g., 10-A" />
              </div>
              <div className="form-group">
                <label>Parent Phone</label>
                <input type="text" name="parentPhone" value={formData.parentPhone} onChange={handleInputChange} placeholder="03001234567" />
              </div>
              <div className="form-group">
                <label>Roll Number</label>
                <input type="text" name="rollNumber" value={formData.rollNumber} onChange={handleInputChange} placeholder="001" />
              </div>
              <div className="form-group">
                <label>Date of Birth</label>
                <DateInput name="dob" value={formData.dob} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Joining Date</label>
                <DateInput name="joiningDate" value={formData.joiningDate} onChange={handleInputChange} />
              </div>
              <div className="form-group">
                <label>Student Mobile Number</label>
                <input type="text" name="studentMobileNumber" value={formData.studentMobileNumber} onChange={handleInputChange} placeholder="9876543212" />
              </div>
              <div className="form-group">
                <label>Board</label>
                <input type="text" name="board" value={formData.board} onChange={handleInputChange} placeholder="CBSE / GSEB" />
              </div>
              <div className="form-group">
                <label>Course</label>
                <select name="course" value={formData.course} onChange={handleInputChange}>
                  <option value="">Select Course</option>
                  <option value="Science">Science</option>
                  <option value="Commerce">Commerce</option>
                  <option value="General">General</option>
                </select>
              </div>
              <div className="form-group">
                <label>Fees</label>
                <input type="text" name="fees" value={formData.fees} onChange={handleInputChange} placeholder="25000" />
              </div>
              <div className="form-group form-group--full">
                <label>School Name</label>
                <input type="text" name="schoolName" value={formData.schoolName} onChange={handleInputChange} placeholder="Enter school name" />
              </div>
              <div className="form-group form-group--full">
                <label>Address</label>
                <input type="text" name="address" value={formData.address} onChange={handleInputChange} placeholder="Enter address" />
              </div>
              <div className="form-group form-group--full">
                <label>Student Image</label>
                <input
                  key={fileInputKey}
                  type="file"
                  accept=".jpg,.jpeg,image/jpeg"
                  onChange={handleImageChange}
                />
                <small className="upload-help">Only JPG and JPEG files are supported.</small>
              </div>
              {formData.imageData && (
                <div className="form-group form-group--full">
                  <img src={formData.imageData} alt="Student preview" className="form-preview-image" />
                </div>
              )}
            </div>
            <div className="button-group">
              <button className="btn btn-primary" onClick={handleAddStudent}>
                {editingId ? "Update Student" : "Add Student"}
              </button>
              <button className="btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading && <p className="empty-state">Loading students...</p>}

        {!loading && students.length === 0 && (
          <p className="empty-state">No students found for the selected class.</p>
        )}

        {!loading && students.length > 0 && filteredStudents.length === 0 && (
          <p className="empty-state">No students found for the selected board or course.</p>
        )}

        {!loading && filteredStudents.length > 0 && viewMode === "card" && (
          <div className="card-grid">
            {filteredStudents.map((student) => {
              const imageSource = student.imageData || student.imageUrl;
              return (
                <article
                  className={`profile-card ${searchSelection?.id === student._id ? "profile-card--highlighted" : ""}`}
                  key={student._id}
                >
                  <button className="profile-card__media profile-card__media-btn" onClick={() => openProfile(student)}>
                    {imageSource ? (
                      <img src={imageSource} alt={student.name} className="profile-card__image" />
                    ) : (
                      <div className="profile-card__placeholder">
                        {student.name?.slice(0, 1)?.toUpperCase() || "S"}
                      </div>
                    )}
                  </button>
                  <div className="profile-card__body">
                    <div className="profile-card__heading">
                      <button className="profile-card__name-btn" onClick={() => openProfile(student)}>
                        {student.name}
                      </button>
                      <span className="profile-card__badge">{student.class}</span>
                    </div>
                    <p><strong>Roll Number:</strong> {student.rollNumber}</p>
                    <p><strong>Parent Phone:</strong> {student.parentPhone}</p>
                    <p><strong>Student Mobile:</strong> {student.studentMobileNumber || "-"}</p>
                    <p><strong>Board:</strong> {student.board || "-"}</p>
                    <p><strong>Course:</strong> {student.course || "-"}</p>
                    <p><strong>Fees:</strong> {student.fees || "-"}</p>
                    <p><strong>School:</strong> {student.schoolName || "-"}</p>
                    <p><strong>DOB:</strong> {formatDate(student.dob)}</p>
                    <p><strong>Joining Date:</strong> {formatDate(student.joiningDate)}</p>
                    <p><strong>Address:</strong> {student.address || "-"}</p>
                  </div>
                  <div className="profile-card__actions">
                    <button className="action-btn edit" onClick={() => handleEditStudent(student)}>Edit</button>
                    <button className="action-btn delete" onClick={() => handleDeleteStudent(student._id)}>Delete</button>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading && filteredStudents.length > 0 && viewMode === "detail" && (
          <div className="detail-table-wrap">
            <table className="detail-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Class</th>
                  <th>Roll Number</th>
                  <th>Parent Phone</th>
                  <th>Student Mobile</th>
                  <th>DOB</th>
                  <th>Joining Date</th>
                  <th>School Name</th>
                  <th>Board</th>
                  <th>Course</th>
                  <th>Fees</th>
                  <th>Address</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student) => {
                  const imageSource = student.imageData || student.imageUrl;
                  return (
                    <tr
                      key={student._id}
                      className={searchSelection?.id === student._id ? "detail-table__row--highlighted" : ""}
                    >
                      <td>
                        {imageSource ? (
                          <button className="detail-table__avatar-btn" onClick={() => openProfile(student)}>
                            <img src={imageSource} alt={student.name} className="detail-table__avatar" />
                          </button>
                        ) : (
                          <button className="detail-table__avatar-btn" onClick={() => openProfile(student)}>
                            <div className="detail-table__placeholder">
                              {student.name?.slice(0, 1)?.toUpperCase() || "S"}
                            </div>
                          </button>
                        )}
                      </td>
                      <td>
                        <button className="detail-table__name-btn" onClick={() => openProfile(student)}>
                          {student.name}
                        </button>
                      </td>
                      <td>{student.class}</td>
                      <td>{student.rollNumber}</td>
                      <td>{student.parentPhone}</td>
                      <td>{student.studentMobileNumber || "-"}</td>
                      <td>{formatDate(student.dob)}</td>
                      <td>{formatDate(student.joiningDate)}</td>
                      <td>{student.schoolName || "-"}</td>
                      <td>{student.board || "-"}</td>
                      <td>{student.course || "-"}</td>
                      <td>{student.fees || "-"}</td>
                      <td>{student.address || "-"}</td>
                      <td>
                        <div className="detail-table__actions">
                          <button className="action-btn edit" onClick={() => handleEditStudent(student)}>Edit</button>
                          <button className="action-btn delete" onClick={() => handleDeleteStudent(student._id)}>Delete</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default StudentList;
